import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { logout } from '../../../auth';
import { Flex } from '../../../base/components/Flex';
import {
  CompanyQrLoginType,
  CompanyQrQuery,
  CreateSessionPlaylistItemParamInput,
  LaunchCompanyQrDocument,
} from '../../../graphql/graphql-operations';
import { useLanguage } from '../../../hooks/useChangeUserLanguage';
import { useMutationWithSnackbar } from '../../../hooks/useMutationWithSnackbar';
import { useTranslationPrefix } from '../../../hooks/useTranslationPrefix';
import LaunchQRLayout from '../../../layouts/ClientLayout/LaunchQRLayout';
import QrLanguageSelector from './components/QrLanguageSelector';
import QrCertificateStep from './steps/QrCertificateStep';
import QrGoodByeStep from './steps/QrGoodByeStep';
import QrHealthAndSafetuStep from './steps/QrHealthAndSafetuStep';
import QrLoadingStep from './steps/QrLoadingStep';
import QrPersonalStep, { QrPersonalData } from './steps/QrPersonalStep';
import QrSceneStep from './steps/QrSceneStep';
import QrStreamingStep from './steps/QrStreamingStep';
import QrVideoStep from './steps/QrVideoStep';
import QrWaitStep from './steps/QrWaitStep';
import QrWelcomeStep from './steps/QrWelcomeStep';
import { QrDeviceState, QrVideoIds, useQrData } from './useQrData';

type Step =
  | 'loading'
  | 'welcome'
  | 'primary-headset-wakeup-video'
  | 'backup-headset-wakeup-video'
  | 'health-and-safety'
  | 'certificate'
  | 'personal'
  | 'scene'
  | 'launch-instruction-video'
  | 'wait'
  | 'primary-headset-instruction-video'
  | 'backup-headset-instruction-video'
  | 'streaming'
  | 'return-headset-instruction-video'
  | 'done';

export type QrData = CompanyQrQuery['companyQr'];
export type QrScene = QrData['qrScenes'][0];
interface Wizzard {
  step: Step;
  personalData: QrPersonalData;
  qrSceneId?: string;
  params: CreateSessionPlaylistItemParamInput[];
  vrDeviceId?: string;
  sessionId?: string;
}

const resolveHeasetInstructionVideoStep = (
  state: QrDeviceState,
  videoIds: QrVideoIds,
): Step | null => {
  if (state.primary && videoIds.primaryHeadsetInstructionVideoId) {
    return 'primary-headset-instruction-video';
  } else if (videoIds.backupHeadsetInstructionVideoId) {
    return 'backup-headset-instruction-video';
  }
  return 'streaming';
};

const reset = async () => {
  await logout();
  window.localStorage.removeItem('i18nextLng');
  // eslint-disable-next-line no-self-assign
  window.location.href = window.location.href;
};

const resolveStepByLoginType = (loginType?: CompanyQrLoginType): Step => {
  if (loginType === CompanyQrLoginType.ANNONYMOUS) {
    return 'scene';
  }
  if (loginType === CompanyQrLoginType.EMAIL) {
    return 'personal';
  }
  return 'certificate';
};

const LaunchCompanyQrPage = () => {
  const { id } = useParams<{ id: string }>();
  const [wizzard, setWizzard] = useState<Wizzard>({
    step: 'loading',
    personalData: { firstName: '', lastName: '', email: '', gdpr: false },
    qrSceneId: undefined,
    params: [],
  });
  const { _t } = useTranslationPrefix('LaunchPlaylistQrPageNew');
  const language = useLanguage();

  const { qrData, videoIds, deviceState } = useQrData(
    id!,
    language,
    wizzard.vrDeviceId,
  );

  console.log('deviceState: ', deviceState);
  const [launchMutation] = useMutationWithSnackbar(
    'LaunchPlaylistQrPageNew.launchMutation',
    LaunchCompanyQrDocument,
  );

  const handleLaunch = async (data: {
    qrSceneId?: string;
    params?: CreateSessionPlaylistItemParamInput[];
  }) => {
    const toLaunchData = {
      personalData: wizzard.personalData,
      qrSceneId: data.qrSceneId || wizzard.qrSceneId,
      params: data.params || wizzard.params,
      vrDeviceId: wizzard.vrDeviceId,
    };

    if (!toLaunchData.qrSceneId) {
      console.error('No qr scene selected, should not happen');
      return;
    }

    if (data.qrSceneId || data.params) {
      setWizzard((w) => ({
        ...w,
        ...data, //update data in wizzard for repeated launch if any chaged
      }));
    }

    const launchResult = await launchMutation({
      variables: {
        input: {
          id: id!,
          firstName: toLaunchData.personalData.firstName || null,
          lastName: toLaunchData.personalData.lastName || null,
          email: toLaunchData.personalData.email || null,
          languageCode: language,
          qrSceneId: toLaunchData.qrSceneId,
          params: toLaunchData.params,
          preselectedVrDeviceId: toLaunchData.vrDeviceId || null,
        },
      },
    });

    if (launchResult.errors) {
      //just save data for relaunch purposes
      setWizzard((w) => ({
        ...w,
        ...data,
      }));
      return;
    }

    setWizzard((w) => {
      const qrScene = qrData?.qrScenes?.find(
        (s) => s.id === (data.qrSceneId || w.qrSceneId),
      );
      let nextStep: Step | null = null;
      if (qrScene?.launchTimoutSeconds) {
        nextStep = 'wait';
      } else {
        nextStep = resolveHeasetInstructionVideoStep(deviceState, videoIds);
      }
      return {
        ...w,
        ...data,
        sessionId: launchResult.data?.launchCompanyQr.id,
        vrDeviceId: launchResult.data?.launchCompanyQr.vrDeviceId,
        step: nextStep || 'streaming',
      };
    });
  };

  useEffect(() => {
    if (!qrData) {
      return;
    }
    if (wizzard.step === 'loading') {
      const nextStep: Step = qrData.lightVersion
        ? 'health-and-safety'
        : 'welcome';
      setWizzard((w) => ({ ...w, step: nextStep }));
    }
  }, [qrData]);

  useEffect(() => {
    if (
      (wizzard.step === 'primary-headset-wakeup-video' ||
        wizzard.step === 'backup-headset-wakeup-video') &&
      deviceState.device?.status.screenOn
    ) {
      setWizzard((w) => ({
        ...w,
        step: 'health-and-safety',
        vrDeviceId: deviceState.device?.id,
      }));
    } else if (
      wizzard.step === 'primary-headset-wakeup-video' &&
      videoIds.backupHeadsetWakeupVideoId &&
      !deviceState.primary
    ) {
      setWizzard((w) => ({ ...w, step: 'backup-headset-wakeup-video' }));
    } else if (
      wizzard.step === 'backup-headset-wakeup-video' &&
      videoIds.primaryHeadsetWakeupVideoId &&
      deviceState.primary
    ) {
      setWizzard((w) => ({ ...w, step: 'primary-headset-wakeup-video' }));
    }

    if (
      (wizzard.step === 'primary-headset-instruction-video' ||
        wizzard.step === 'backup-headset-instruction-video') &&
      !deviceState.device?.status.charging
    ) {
      setWizzard((w) => ({ ...w, step: 'streaming' }));
    }
    if (
      wizzard.step === 'return-headset-instruction-video' &&
      deviceState.device?.status.charging
    ) {
      setWizzard((w) => ({ ...w, step: 'done' }));
    }
  }, [
    deviceState.primary,
    deviceState.device?.status.screenOn,
    deviceState.device?.status.charging,
    wizzard.step,
  ]);

  return (
    <LaunchQRLayout>
      <Flex
        flexDirection="column"
        alignItems="center"
        flexGrow={1}
        width="100%"
      >
        <Flex
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          maxWidth="100%"
          width="100%"
          flexGrow={1}
          py={2}
        >
          {wizzard.step === 'loading' ? (
            <QrLoadingStep />
          ) : wizzard.step === 'welcome' ? (
            <QrWelcomeStep
              onNextClick={() =>
                setWizzard((w) => {
                  let nextStep: Step = 'health-and-safety';

                  if (!deviceState.device?.status.screenOn) {
                    if (
                      deviceState.primary &&
                      videoIds.primaryHeadsetWakeupVideoId
                    ) {
                      nextStep = 'primary-headset-wakeup-video';
                    } else if (
                      !deviceState.primary &&
                      videoIds.backupHeadsetWakeupVideoId
                    ) {
                      nextStep = 'backup-headset-wakeup-video';
                    }
                  }
                  return {
                    ...w,
                    step: nextStep,
                  };
                })
              }
            />
          ) : wizzard.step === 'primary-headset-wakeup-video' ? (
            <QrVideoStep
              videoId={videoIds.primaryHeadsetWakeupVideoId}
              loop={true}
              subtitle={_t('wakeUpPrimary', 'Wake up left device')}
              instruction={_t('wakeUpPrimaryFallback', '')}
              onNext={() =>
                setWizzard((w) => ({ ...w, step: 'health-and-safety' }))
              }
              direction="left"
            />
          ) : wizzard.step === 'backup-headset-wakeup-video' ? (
            <QrVideoStep
              videoId={videoIds.backupHeadsetWakeupVideoId}
              loop={true}
              subtitle={_t('wakeUpBackup', 'Wake up right device')}
              instruction={_t('wakeUpBackupFallback', '')}
              onNext={() =>
                setWizzard((w) => ({ ...w, step: 'health-and-safety' }))
              }
              direction="right"
            />
          ) : wizzard.step === 'health-and-safety' ? (
            <QrHealthAndSafetuStep
              onYesClick={() =>
                setWizzard((w) => ({
                  ...w,
                  step: resolveStepByLoginType(qrData?.loginType),
                }))
              }
              onNoClick={reset}
            />
          ) : wizzard.step === 'certificate' ? (
            <QrCertificateStep
              onYesClick={() => setWizzard((w) => ({ ...w, step: 'personal' }))}
              onNoClick={() => setWizzard((w) => ({ ...w, step: 'scene' }))}
            />
          ) : wizzard.step === 'personal' ? (
            <QrPersonalStep
              data={wizzard.personalData}
              onSubmit={(data) =>
                setWizzard((w) => ({
                  ...w,
                  personalData: data,
                  step: 'scene',
                }))
              }
            />
          ) : wizzard.step === 'scene' ? (
            <QrSceneStep
              qrScenes={qrData?.qrScenes || []}
              onNext={(data) => {
                if (videoIds.launchInstructionVideoId) {
                  setWizzard((w) => ({
                    ...w,
                    qrSceneId: data.qrSceneId,
                    params: data.params,
                    //nabigate to video or wait page based on data
                    step: 'launch-instruction-video',
                  }));
                } else {
                  handleLaunch(data);
                }
              }}
            />
          ) : wizzard.step === 'launch-instruction-video' ? (
            <QrVideoStep
              videoId={videoIds.launchInstructionVideoId}
              subtitle={_t('launchInstructionVideo', 'Video instructions')}
              instruction={_t('launchInstructionVideoFallback', '')}
              onNext={() => handleLaunch({})}
            />
          ) : wizzard.step === 'wait' ? (
            <QrWaitStep
              seconds={
                qrData?.qrScenes?.find((s) => s.id === wizzard.qrSceneId)
                  ?.launchTimoutSeconds || 0
              }
              onNext={() =>
                setWizzard((w) => {
                  const videoStep = resolveHeasetInstructionVideoStep(
                    deviceState,
                    videoIds,
                  );
                  return {
                    ...w,
                    step: videoStep || 'streaming',
                  };
                })
              }
            />
          ) : wizzard.step === 'primary-headset-instruction-video' ? (
            <QrVideoStep
              videoId={videoIds.primaryHeadsetInstructionVideoId}
              direction="left"
              loop={true}
              subtitle={_t(
                'primaryHeadsetInstructionVideo',
                'Put left headset on',
              )}
              instruction={_t('primaryHeadsetInstructionVideoFallback', '')}
              onNext={() =>
                setWizzard((w) => ({
                  ...w,
                  step: 'streaming',
                }))
              }
            />
          ) : wizzard.step === 'backup-headset-instruction-video' ? (
            <QrVideoStep
              videoId={videoIds.backupHeadsetInstructionVideoId}
              direction="right"
              loop={true}
              subtitle={_t(
                'backupHeadsetInstructionVideo',
                'Put right headset on',
              )}
              instruction={_t('backupHeadsetInstructionVideoFallback', '')}
              onNext={() =>
                setWizzard((w) => ({
                  ...w,
                  step: 'streaming',
                }))
              }
            />
          ) : wizzard.step === 'streaming' ? (
            <QrStreamingStep
              sessionId={wizzard.sessionId!}
              onNext={() =>
                setWizzard((w) => ({
                  ...w,
                  step:
                    videoIds.returnHeadsetInstructionVideoId &&
                    !deviceState.device?.status.charging
                      ? 'return-headset-instruction-video'
                      : 'done',
                }))
              }
              onLaunchAgain={() => handleLaunch({})}
            />
          ) : wizzard.step === 'return-headset-instruction-video' ? (
            <QrVideoStep
              videoId={videoIds.returnHeadsetInstructionVideoId}
              subtitle={_t('returnHeadsetInstructionVideo', 'Return headset')}
              loop={true}
              instruction={_t('returnHeadsetInstructionVideoFallback', '')}
              onNext={() =>
                setWizzard((w) => ({
                  ...w,
                  step: 'done',
                }))
              }
            />
          ) : wizzard.step === 'done' ? (
            <QrGoodByeStep onNext={reset} />
          ) : (
            <>Unknown step {wizzard.step}</>
          )}
        </Flex>
      </Flex>
      {(wizzard.step === 'welcome' ||
        (wizzard.step === 'health-and-safety' && qrData?.lightVersion)) && (
        <QrLanguageSelector />
      )}
    </LaunchQRLayout>
  );
};

export default LaunchCompanyQrPage;
